import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  type ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { FINO_VERSION } from '@fino-ui/common';
import { ThemingService } from '@fino-ui/theming';
import {
  FINO_TRANSLATION_CONFIG,
  provideTranslationModule,
  TranslateService,
} from '@fino-ui/translation';
import { createErrorHandler } from '@sentry/angular';
import { Version } from '@version';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { ToastrModule } from 'ngx-toastr';
import {
  provideNgxWebstorage,
  SessionStorageService,
  withLocalStorage,
  withNgxWebstorageConfig,
  withSessionStorage,
} from 'ngx-webstorage';
import { routes } from './app.routes';
import { AppService } from './app.service';
import { AuthHttpInterceptor } from './core/auth/auth.http.interceptor';
import { loadConfig } from './core/environment/config.loader';
import { LoadingHttpInterceptor } from './core/interceptors/loading.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      KeycloakAngularModule,
      provideTranslationModule(),
      ToastrModule.forRoot(),
      NgxLoadingModule.forRoot({
        animationType: ngxLoadingAnimationTypes.circleSwish,
      }),
    ),
    {
      provide: FINO_VERSION,
      useValue: Version,
    },
    provideAngularSvgIcon(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNgxWebstorage(
      withNgxWebstorageConfig({
        caseSensitive: true,
        // NOTE: The prefix and separator options lead to the following error:
        // The tenant loadConfig will not work correctly if the prefix or separator is set.
        // It will load the default configuration instead of the tenant-specific configuration.
        // prefix: 'uba',
        // separator: '.',
      }),
      withLocalStorage(),
      withSessionStorage(),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingHttpInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: FINO_TRANSLATION_CONFIG,
      useValue: {
        availableLanguages: ['de'],
      },
    },
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        // Reload the page if the same URL is requested
        onSameUrlNavigation: 'reload',
        // Bind route info to component inputs (e.g. route.params)
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideNgxSkeletonLoader({
      theme: {
        extendsFromRoot: true,
        height: '30px',
      },
    }),
    provideAppInitializer(() => {
      const initializerFn = loadConfig(
        inject(HttpClient),
        inject(KeycloakService),
        inject(AppService),
        inject(ThemingService),
        inject(TranslateService),
        inject(SessionStorageService),
      );
      return initializerFn();
    }),
  ],
};
